import fixWebmDuration from "fix-webm-duration";
import { MAX_RECORDING_MS } from "@msuite/utils/videos";
import { create } from "zustand";
import { type CameraBubbleCompositor, composeScreenWithCamera } from "./camera-bubble";
import { closePipWindow } from "./pip-window";
import { claimRecording, releaseRecording } from "./recording-claim";
import { captureFrameSize, pickRecordingMimeType } from "./recording-codec";
import { persistence } from "./recording-persistence";
import { installUnloadGuard } from "./unload-guard";

export type VideoRecordingType = "screen" | "screen-camera" | "screen-silent" | "camera";

/**
 * Where a recording stands. `idle` covers both "nothing yet" and "finished, blob in hand";
 * `assembling` is the window after Stop while the container is written, during which the
 * recording exists but the blob does not yet.
 */
export type RecorderStatus =
	| "idle"
	| "recording"
	| "paused"
	| "assembling"
	| "uploading"
	| "uploaded"
	| "failed";

/**
 * Thrown when the author asked for narration and the microphone could not be opened. It is
 * distinct from a cancelled surface picker, which is not a failure and needs no message.
 */
export class MicrophoneUnavailableError extends Error {
	constructor(cause: unknown) {
		super("The microphone could not be opened", { cause });
		this.name = "MicrophoneUnavailableError";
	}
}

/**
 * Thrown when the camera source could not be opened. Separate from the microphone case because
 * the remedy differs — a blocked camera and a blocked microphone are two different browser
 * permissions, and telling the author to fix the wrong one wastes the attempt.
 */
export class CameraUnavailableError extends Error {
	constructor(cause: unknown) {
		super("The camera could not be opened", { cause });
		this.name = "CameraUnavailableError";
	}
}

/**
 * `systemAudio` is part of the Screen Capture specification but absent from TypeScript's DOM
 * library, so the options carry their own type rather than a cast at the call site.
 */
interface DisplayOptions extends DisplayMediaStreamOptions {
	systemAudio?: "include" | "exclude";
}

/**
 * Where a finished recording belongs, and how to ship it there.
 *
 * The session is told this at start rather than discovering it at stop, because by then the
 * author may be on another page entirely. Keeping it as data — an id, a label, a link and an
 * upload function supplied by the host — is what keeps this package free of the API client and
 * of any knowledge about wiki pages.
 */
export interface RecordingTarget {
	/** The row the recording uploads against, created before the capture began. */
	videoId: string;
	/** What to call the destination on the "back to…" control. */
	label: string;
	/** Where that control leads. */
	href: string;
	/**
	 * `${orgId}:${userId}` of the session that made it, stored alongside a recording so a rescued
	 * one is offered back only to the identity that can still reach its destination. Optional: a
	 * host with no notion of identity simply gets no ownership check.
	 */
	owner?: string;
	/** Ships the finished recording. Reports progress as a fraction between 0 and 1. */
	upload: (blob: Blob, onProgress: (fraction: number) => void) => Promise<void>;
	/**
	 * Undoes the destination. Called when no recording will ever arrive there — the capture
	 * failed to open, or the author discarded what was captured — because a destination prepared
	 * in advance is visible to everyone until something takes it back.
	 */
	abandon?: () => Promise<unknown>;
}

/** One recording's streams and the teardown for everything behind them. */
interface OpenedStream {
	stream: MediaStream;
	/**
	 * The capture tracks behind `stream`, which for every source but one are its own video tracks.
	 * The camera-bubble source is the exception: there the recorded track comes out of a
	 * compositor and has no relationship to the surfaces it was drawn from, so it does not end
	 * when the author clicks the browser's "Stop sharing".
	 */
	sourceTracks: MediaStreamTrack[];
	/** Stops the compositor drawing while a recording is paused. Absent where nothing composites. */
	setPaused?: (isPaused: boolean) => void;
	stop: () => void;
}

/**
 * Above this the container fix is skipped. It reads the whole recording into one contiguous
 * buffer and allocates a second, so a long tutorial would take the tab down — and the fix only
 * serves the local preview anyway, since Mux writes the true duration at ingest.
 */
const DURATION_FIX_SIZE_LIMIT = 150_000_000;

function stopTracks(stream: MediaStream) {
	for (const track of stream.getTracks()) {
		track.stop();
	}
}

/**
 * Merge several audio tracks into one. `MediaRecorder` writes a single audio track, so a
 * recording that carries both the narration and the shared surface's own sound has to mix them
 * beforehand — handing over two tracks silently keeps one and drops the other.
 *
 * @param tracks - The live audio tracks to merge.
 * @returns The merged track and the context owning it, which the caller closes on stop.
 */
function mixAudioTracks(tracks: MediaStreamTrack[]) {
	const context = new AudioContext();
	const destination = context.createMediaStreamDestination();
	for (const track of tracks) {
		context.createMediaStreamSource(new MediaStream([track])).connect(destination);
	}
	return { track: destination.stream.getAudioTracks().at(0), context };
}

/**
 * Name the device that refused, when one call asked for both.
 *
 * The camera and the microphone are opened together so the author answers a single prompt, and a
 * rejection then names neither of them. Telling someone their camera is blocked when it is their
 * microphone costs them however long it takes to discover that, so the permission states are read
 * back — that needs no device and raises no prompt — and the error names what they say.
 *
 * @param cause - The rejection `getUserMedia` produced.
 * @returns The error to throw.
 */
async function classifyCaptureFailure(cause: unknown) {
	try {
		const camera = await navigator.permissions.query({ name: "camera" as PermissionName });
		if (camera.state === "denied") return new CameraUnavailableError(cause);
		const microphone = await navigator.permissions.query({
			name: "microphone" as PermissionName,
		});
		if (microphone.state === "denied") return new MicrophoneUnavailableError(cause);
	} catch {
		// Neither name is in every browser's permission registry, and a rejection can come from a
		// device that is missing rather than refused. The camera is the likelier of the two and
		// the one these sources are named for, so it stays the answer.
	}
	return new CameraUnavailableError(cause);
}

/**
 * Open the capture streams for one source and compose what the recorder actually records.
 *
 * Order matters: the display is requested first because `getDisplayMedia` consumes the click's
 * transient activation, which a slowly answered microphone prompt would outlive. System audio is
 * excluded so a tutorial cannot pick up notification sounds — the shared surface's own audio
 * stays reachable through the picker's checkbox and is mixed in when the author ticks it.
 */
async function openStream(source: VideoRecordingType): Promise<OpenedStream> {
	if (source === "camera") {
		// The screen path below refuses rather than record silently; this option promises
		// narration too, so a refused camera or microphone has to end the attempt the same way
		// instead of leaving the start modal sitting there with no explanation.
		let camera: MediaStream;
		try {
			camera = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
		} catch (error) {
			throw await classifyCaptureFailure(error);
		}
		return {
			stream: camera,
			sourceTracks: camera.getVideoTracks(),
			stop: () => stopTracks(camera),
		};
	}

	const displayOptions: DisplayOptions = {
		video: true,
		audio: source === "screen" || source === "screen-camera",
		systemAudio: "exclude",
	};
	const display = await navigator.mediaDevices.getDisplayMedia(displayOptions);

	if (source === "screen-silent") {
		return {
			stream: new MediaStream(display.getVideoTracks()),
			sourceTracks: display.getVideoTracks(),
			stop: () => stopTracks(display),
		};
	}

	if (source === "screen-camera") return await openComposedStream(display);

	let microphone: MediaStream;
	try {
		microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
	} catch (error) {
		// Recording silently when narration was asked for is the one failure the author cannot
		// notice until the tutorial is finished, so it ends the attempt instead.
		stopTracks(display);
		throw new MicrophoneUnavailableError(error);
	}

	const audioTracks = [...microphone.getAudioTracks(), ...display.getAudioTracks()];
	const mixed = audioTracks.length > 1 ? mixAudioTracks(audioTracks) : null;
	const audioTrack = mixed?.track ?? audioTracks.at(0);

	return {
		stream: new MediaStream([...display.getVideoTracks(), ...(audioTrack ? [audioTrack] : [])]),
		sourceTracks: display.getVideoTracks(),
		stop: () => {
			stopTracks(display);
			stopTracks(microphone);
			// The mixed track belongs to no source stream, so the two calls above do not reach it.
			mixed?.track?.stop();
			mixed?.context.close().catch((error) => {
				console.error("Closing the recording's audio context failed:", error);
			});
		},
	};
}

/**
 * Open the camera beside an already-shared surface and compose the two into one track.
 *
 * The camera is asked for narration in the same call as the picture, so this path spends one
 * permission prompt where opening them separately would spend two. A refused camera ends the
 * attempt rather than falling back to a plain screen recording: the author chose this source for
 * the bubble, and a tutorial that quietly does not have one is noticed after it is finished.
 *
 * @param display - The surface the author already picked.
 * @returns The composed stream, the capture tracks behind it, and the teardown for all of it.
 */
async function openComposedStream(display: MediaStream): Promise<OpenedStream> {
	let camera: MediaStream;
	try {
		camera = await navigator.mediaDevices.getUserMedia({
			// Constrained to roughly what the bubble can show. The camera's own cadence becomes the
			// recording's — every frame it delivers composes one — so a webcam left to negotiate
			// 4K in a dim room would set the whole tutorial's frame rate.
			video: { height: { ideal: 720 }, frameRate: { ideal: 30 } },
			audio: true,
		});
	} catch (error) {
		stopTracks(display);
		throw await classifyCaptureFailure(error);
	}

	const screenTrack = display.getVideoTracks().at(0);
	const cameraTrack = camera.getVideoTracks().at(0);
	if (!screenTrack || !cameraTrack) {
		stopTracks(display);
		stopTracks(camera);
		throw new CameraUnavailableError(new Error("The capture produced no video track"));
	}

	// Until the object below is returned, nothing anywhere is holding these two captures, so
	// anything that throws in between strands the browser's sharing bar and the camera light with
	// no control able to stop either.
	let compositor: CameraBubbleCompositor | null = null;
	try {
		compositor = composeScreenWithCamera(screenTrack, cameraTrack);
		const audioTracks = [...camera.getAudioTracks(), ...display.getAudioTracks()];
		const mixed = audioTracks.length > 1 ? mixAudioTracks(audioTracks) : null;
		const audioTrack = mixed?.track ?? audioTracks.at(0);
		const composition = compositor;

		return {
			stream: new MediaStream([composition.track, ...(audioTrack ? [audioTrack] : [])]),
			sourceTracks: [screenTrack, cameraTrack],
			setPaused: composition.setPaused,
			stop: () => {
				// The compositor first: it holds frames from both captures, and stopping the tracks
				// underneath it would leave those to be collected rather than released.
				composition.stop();
				stopTracks(display);
				stopTracks(camera);
				mixed?.track?.stop();
				mixed?.context.close().catch((error) => {
					console.error("Closing the recording's audio context failed:", error);
				});
			},
		};
	} catch (error) {
		compositor?.stop();
		stopTracks(display);
		stopTracks(camera);
		throw error;
	}
}

/**
 * The live machinery of one recording. It lives in module scope on purpose: a React remount —
 * an organisation switch re-keys the provider tree — would otherwise orphan the `MediaRecorder`
 * while it keeps writing, and React 19 makes the resulting `setState` a silent no-op, so a
 * finished recording would vanish without an error.
 */
interface Session {
	recorder: MediaRecorder;
	chunks: Blob[];
	stopSources: () => void;
	/** Stops the compositor drawing while paused. Absent for the sources that compose nothing. */
	setPaused?: (isPaused: boolean) => void;
	/** Distinguishes this recording from a later one, so a stale callback cannot touch it. */
	token: number;
}

let session: Session | null = null;
let nextToken = 1;
/**
 * Which recording the store currently belongs to. Bumped when one is discarded and when a new
 * one takes over, and captured by anything that runs across an await.
 *
 * Two things need it. `startRecording` waits on the surface picker, which can stand open for
 * many seconds — long enough for the author to close the modal — so a discard has to be able to
 * reach a capture that has not begun yet; without that the stream would open afterwards with no
 * interface anywhere to stop it. And an upload outlives the recording it came from: if it
 * settles after the author has discarded it or started the next one, it must not write its
 * result over a store that has moved on.
 */
let flightGeneration = 0;

interface RecorderState {
	status: RecorderStatus;
	video: Blob | null;
	videoUrl: string | null;
	/** `performance.now()` when recording began — monotonic, unlike a wall clock. */
	startedAtMs: number | null;
	/** Total time spent paused, closed off at each resume. */
	accumulatedPausedMs: number;
	/** When the current pause began; null while running. */
	pausedAtMs: number | null;
	isSourcePickerOpen: boolean;
	/** Where this recording belongs; null when nothing is in flight. */
	target: RecordingTarget | null;
	/** Upload progress as a fraction between 0 and 1. */
	progress: number;
	/** Whether the last recording ended because it reached the length limit rather than by hand. */
	didReachLimit: boolean;
}

const INITIAL: RecorderState = {
	status: "idle",
	video: null,
	videoUrl: null,
	startedAtMs: null,
	accumulatedPausedMs: 0,
	pausedAtMs: null,
	isSourcePickerOpen: false,
	target: null,
	progress: 0,
	didReachLimit: false,
};

/**
 * The recording state React is allowed to see. Components read it; nothing outside this module
 * writes it. Keeping the blob and the object URL here means one URL per recording rather than
 * one per component, and a single place that revokes it.
 */
export const useRecorderStore = create<RecorderState>()(() => ({ ...INITIAL }));

/**
 * How long the current recording has actually been running, paused time removed.
 *
 * Derived from timestamps on every call rather than accumulated by a ticker: a background tab
 * clamps timers to roughly one second with a spending budget, which is precisely the state this
 * feature puts the tab in, so a counted value would drift and then jump on refocus.
 *
 * @returns Milliseconds of active recording, 0 when nothing is running.
 */
export function getElapsedMs(now = performance.now()): number {
	const { startedAtMs, accumulatedPausedMs, pausedAtMs } = useRecorderStore.getState();
	if (startedAtMs === null) return 0;
	const pausedRightNow = pausedAtMs === null ? 0 : now - pausedAtMs;
	return Math.max(0, now - startedAtMs - accumulatedPausedMs - pausedRightNow);
}

/** Replaces the held recording and keeps exactly one object URL alive for it. */
function setVideo(blob: Blob | null) {
	const previous = useRecorderStore.getState().videoUrl;
	if (previous) URL.revokeObjectURL(previous);
	useRecorderStore.setState({ video: blob, videoUrl: blob ? URL.createObjectURL(blob) : null });
}

/**
 * Assemble the finished recording. The duration is patched in from the measured active time,
 * not from the wall clock, because a paused recording is shorter than the span it covers — a
 * header claiming otherwise makes the preview scrub past the end. A failure here still yields
 * an uploadable blob: Mux writes the true duration when it ingests.
 */
async function assembleRecording(chunks: Blob[], activeMs: number, mimeType = "video/webm") {
	const recorded = new Blob(chunks, { type: mimeType });
	if (recorded.size > DURATION_FIX_SIZE_LIMIT) return recorded;
	try {
		return await fixWebmDuration(recorded, activeMs, { logger: false });
	} catch (error) {
		console.error("Patching the recording's duration failed:", error);
		return recorded;
	}
}

/**
 * Ship a finished recording to where it belongs.
 *
 * Driven from module scope rather than from a component, because the author is expected to be
 * somewhere else entirely by now — a route change must not cancel an upload in flight. The
 * session is released on success so the next recording can begin; the card stays on screen
 * until the author dismisses it.
 */
async function runUpload(blob: Blob, target: RecordingTarget) {
	const generation = flightGeneration;
	// Every write below asks this first. An upload can outlive its recording — the author may
	// discard it, or record something else while it is still travelling — and the outcome of a
	// recording nobody is waiting for any more must not land on the one they are watching.
	const ownsFlight = () => flightGeneration === generation;

	useRecorderStore.setState({ status: "uploading", progress: 0 });
	try {
		await target.upload(blob, (fraction) => {
			if (ownsFlight()) {
				useRecorderStore.setState({ progress: Math.min(1, Math.max(0, fraction)) });
			}
		});
	} catch (error) {
		console.error("Uploading the recording failed:", error);
		if (!ownsFlight()) return;
		session = null;
		useRecorderStore.setState({ status: "failed" });
		return;
	}
	if (!ownsFlight()) return;
	session = null;
	// The bytes are Mux's now, so the copy on disk has nothing left to rescue. The failure branch
	// above deliberately keeps its record: `failed` is the state recovery exists for.
	persistence.discard(target.videoId);
	releaseRecording(target.videoId);
	useRecorderStore.setState({ status: "uploaded", progress: 1 });
}

/**
 * Send the finished recording again after a failed attempt. The direct upload is resumable and
 * the host asks for the same one, so this continues rather than starting over.
 */
export async function retryUpload() {
	const { video, target, status } = useRecorderStore.getState();
	if (status !== "failed" || !video || !target) return;
	await runUpload(video, target);
}

/**
 * Begin a recording from the chosen source.
 *
 * Refuses while one is already running. Without that guard a second start would clear the chunk
 * array the first is still writing into, interleaving two WebM streams into one unplayable file
 * and leaving the first screen share open with nothing holding a reference to it.
 *
 * **The capture is opened before the destination is prepared, and the order is load-bearing.**
 * `getDisplayMedia` spends the click's transient activation, which lasts about five seconds;
 * preparing a destination means several network round trips, and doing them first pushed the
 * capture outside that window on any slow connection — where it fails with an error that reads
 * nothing like "you waited too long". Nothing is recorded before the destination exists, because
 * `recorder.start()` is the last thing that happens; a preparation that fails tears the capture
 * down again.
 *
 * @param source - Which capture to open.
 * @param prepare - Creates the destination once the capture is open. Returning null abandons the
 * attempt and closes the capture.
 * @returns Whether a recording actually began.
 * @throws MicrophoneUnavailableError when narration was asked for and the microphone is refused.
 */
export async function startRecording(
	source: VideoRecordingType,
	prepare: () => Promise<RecordingTarget | null>,
) {
	if (!canStartRecording()) return false;
	const generation = flightGeneration;
	const { stream, sourceTracks, setPaused, stop } = await openStream(source);
	if (session || generation !== flightGeneration) {
		// Either two starts raced through the picker and this one lost, or the author discarded
		// while it stood open. Both mean this capture has no owner: tear it down.
		stop();
		return false;
	}

	let target: RecordingTarget | null;
	try {
		target = await prepare();
	} catch (error) {
		stop();
		throw error;
	}
	// A capture that ended while the destination was being prepared has already fired its `ended`
	// event, and the handlers that would have heard it are bound further down. Its state is the
	// only remaining evidence — without this the recording starts against a dead video track and
	// runs on indefinitely, writing sound alone, with the sharing bar already gone and nothing on
	// screen saying so.
	const hasEndedCapture = [...sourceTracks, ...stream.getVideoTracks()].some(
		(track) => track.readyState === "ended",
	);
	if (!target || session || generation !== flightGeneration || hasEndedCapture) {
		closePipWindow();
		stop();
		void target?.abandon?.().catch((error) => {
			console.error("Undoing the prepared destination failed:", error);
		});
		return false;
	}

	// This recording is now the one the store belongs to, which retires any upload still in
	// flight from the last one.
	flightGeneration++;
	const token = nextToken++;
	const chunks: Blob[] = [];
	// Decided per capture, not per browser: `isTypeSupported` says yes to H.264 whatever the
	// picture, and a frame past level 5.2 — a HiDPI monitor in a scaled mode — kills the recorder
	// on its first chunk. The screen's own size decides, read before a single frame is encoded.
	const frame = captureFrameSize([...sourceTracks, ...stream.getVideoTracks()]);
	const mimeType = pickRecordingMimeType(frame);
	let recorder: MediaRecorder;
	try {
		recorder = new MediaRecorder(stream, { mimeType: mimeType ?? undefined });
	} catch (error) {
		// Constructing the recorder is the last thing that can fail. Leaving the capture open
		// here would strand the browser's sharing bar with nothing able to stop it, and leaving
		// the destination behind would strand a placeholder nothing will ever fill.
		stop();
		void target.abandon?.().catch((undoError) => {
			console.error("Undoing the prepared destination failed:", undoError);
		});
		throw error;
	}
	session = { recorder, chunks, stopSources: stop, setPaused, token };
	// Read out before the callbacks below close over it: `target` is a `let` narrowed by the early
	// return above, and TypeScript discards that narrowing inside a closure created afterwards.
	const { videoId, label, href, owner } = target;
	void claimRecording(videoId);
	persistence.begin({
		videoId,
		label,
		href,
		owner: owner ?? "",
		mimeType: recorder.mimeType,
		startedAtWallMs: Date.now(),
	});

	setVideo(null);
	useRecorderStore.setState({
		status: "recording",
		target,
		progress: 0,
		startedAtMs: performance.now(),
		accumulatedPausedMs: 0,
		pausedAtMs: null,
		// Closed here rather than by the modal, which by now may have been unmounted along with
		// the editor it lived in — leaving the picker flagged open and popping it up unbidden the
		// next time that editor mounts.
		isSourcePickerOpen: false,
		didReachLimit: false,
	});

	recorder.ondataavailable = (event) => {
		if (event.data.size === 0) return;
		chunks.push(event.data);
		const activeMs = getElapsedMs();
		// Fire and forget, in that order: the array above is the recording, the disk is its shadow.
		persistence.append(event.data, activeMs);
		// The length limit is enforced from the recorder's own chunks rather than from a timer,
		// and the reason is the case this feature exists for: recording while working in another
		// application. A hidden tab throttles `setTimeout` to about once a minute, so a scheduled
		// stop would arrive late exactly when a recording is most likely to run long, while chunks
		// keep arriving on time because the recorder is not a timer. It also needs no bookkeeping
		// across a pause — a paused recorder delivers nothing, so this counts recorded material by
		// construction. The cost is that a recording may overrun by up to one chunk, a second.
		if (activeMs >= MAX_RECORDING_MS) {
			useRecorderStore.setState({ didReachLimit: true });
			stopRecording();
		}
	};
	// A recorder that dies while the tab lives stops delivering chunks without ever calling
	// `onstop`, so the record has to be closed off here or it keeps a claim nothing will release.
	recorder.onerror = (event) => {
		console.error("The recorder failed:", event);
		persistence.finish();
	};
	recorder.onstop = async () => {
		if (session?.token !== token) return;
		const activeMs = getElapsedMs();
		persistence.finish();
		stop();
		// The session stays claimed across the await below. That is what makes `startRecording`
		// refuse a second take while the container is still being written, and it is what lets
		// the check after the await tell "still mine" from "discarded meanwhile".
		useRecorderStore.setState({
			status: "assembling",
			startedAtMs: null,
			accumulatedPausedMs: 0,
			pausedAtMs: null,
		});
		const blob = await assembleRecording(chunks, activeMs, recorder.mimeType);
		if (session?.token !== token) return;
		setVideo(blob);
		const { target } = useRecorderStore.getState();
		if (!target) {
			session = null;
			useRecorderStore.setState({ status: "idle" });
			return;
		}
		await runUpload(blob, target);
	};

	// The browser's own "stop sharing" UI ends the capture without touching our controls. Bound
	// over the capture tracks *and* the recorded one, because with a compositor between them the
	// two are different tracks: the sharing bar ends the screen, which the recorded track outlives,
	// and a compositor that collapses ends the recorded track while both captures stay live.
	// Each handler is bound to this session's token, so a track outliving its recording cannot
	// stop a later one. Only what ends from here on is heard — anything that ended earlier is
	// caught by the state check above instead.
	for (const track of new Set([...sourceTracks, ...stream.getVideoTracks()])) {
		track.onended = () => {
			if (session?.token !== token) return;
			if (recorder.state !== "inactive") recorder.stop();
		};
	}

	recorder.start(1000);
	return true;
}

/** Finish the recording and assemble the blob. A no-op when nothing is running. */
export function stopRecording() {
	if (session && session.recorder.state !== "inactive") session.recorder.stop();
}

/**
 * Suspend the recording. The paused span simply does not appear in the output — which is what
 * makes this the cheapest way to cut a fumble out of the middle of a tutorial, since the
 * material never exists anywhere.
 */
export function pauseRecording() {
	if (!session || session.recorder.state !== "recording") return;
	session.recorder.pause();
	// After the recorder's own transition, never before it: the state change must not wait on a
	// compositor. Drawing stops, both captures stay open — the sharing bar and the camera light
	// keep saying what is true, that a paused recording has not let go of anything.
	session.setPaused?.(true);
	useRecorderStore.setState({ status: "paused", pausedAtMs: performance.now() });
}

/** Continue a suspended recording, closing off the pause in the elapsed bookkeeping. */
export function resumeRecording() {
	if (!session || session.recorder.state !== "paused") return;
	session.recorder.resume();
	session.setPaused?.(false);
	const { accumulatedPausedMs, pausedAtMs } = useRecorderStore.getState();
	useRecorderStore.setState({
		status: "recording",
		accumulatedPausedMs:
			accumulatedPausedMs + (pausedAtMs === null ? 0 : performance.now() - pausedAtMs),
		pausedAtMs: null,
	});
}

/** Discard everything: a running recording, the held blob, and the source picker's state. */
export function resetRecording() {
	closePipWindow();
	const { target, status } = useRecorderStore.getState();
	// A recording that reached Mux is real and stays; anything short of that leaves a
	// placeholder on the page, so discarding has to take the destination with it.
	if (target && status !== "uploaded") {
		void target.abandon?.().catch((error) => {
			console.error("Undoing the prepared destination failed:", error);
		});
	}
	if (target) {
		// Covers all three ways a recording ends without shipping — discarded, dismissed after a
		// success, or replaced. In every one of them the copy on disk has no author waiting for it.
		persistence.discard(target.videoId);
		releaseRecording(target.videoId);
	}
	flightGeneration++;
	if (session) {
		const current = session;
		session = null;
		if (current.recorder.state !== "inactive") current.recorder.stop();
		current.stopSources();
	}
	setVideo(null);
	useRecorderStore.setState({ ...INITIAL });
}

/**
 * Whether a new recording may begin.
 *
 * A held recording that never reached Mux is the reason this is not simply "no session running".
 * A failed upload releases the session but keeps the only copy of the take in memory, behind the
 * card's Retry button — and starting the next recording drops that blob and revokes its object
 * URL. An hour of tutorial would go with it, silently. From `idle` and `uploaded` there is
 * nothing to lose; from `failed` the author has to retry or discard first, and the card offers
 * both.
 */
export function canStartRecording() {
	if (session) return false;
	const { status } = useRecorderStore.getState();
	return status === "idle" || status === "uploaded";
}

/** Open the source picker. Refused while a recording runs, which is what the panel is for. */
export function openSourcePicker() {
	if (!canStartRecording()) return;
	useRecorderStore.setState({ isSourcePickerOpen: true });
}

/** Close the source picker without touching the recording. */
export function closeSourcePicker() {
	useRecorderStore.setState({ isSourcePickerOpen: false });
}

/**
 * Take a recording rescued from disk into the store as if it had just been stopped.
 *
 * It enters at `assembling` and travels the ordinary path from there, which is why nothing about
 * the card, the progress meter, Retry or Discard needs to know that this take was ever lost. No
 * `Session` is constructed, because there is no `MediaRecorder` to put in one — `canStartRecording`
 * already refuses from any status but `idle` and `uploaded`, so the store alone holds the next
 * take off until this one is done.
 *
 * @param target - Where the rescued recording ships, rebuilt by the host from stored data.
 * @param chunks - The contiguous chunks read back from disk.
 * @param durationMs - Active milliseconds the chunks cover, as recorded alongside them.
 * @param mimeType - The container the chunks were written in.
 * @returns Whether the recording was adopted; false when the recorder is busy with another.
 */
export async function adoptRecoveredRecording(
	target: RecordingTarget,
	chunks: Blob[],
	durationMs: number,
	mimeType: string,
) {
	if (!canStartRecording()) return false;
	const generation = ++flightGeneration;
	persistence.adopt(target.videoId);
	setVideo(null);
	useRecorderStore.setState({
		status: "assembling",
		target,
		progress: 0,
		startedAtMs: null,
		accumulatedPausedMs: 0,
		pausedAtMs: null,
		isSourcePickerOpen: false,
	});
	const blob = await assembleRecording(chunks, durationMs, mimeType);
	// Writing the container takes time, and the author can discard in it. By then the destination
	// and the copy on disk are already gone, so shipping anyway would leave the recorder in an
	// upload the panel no longer draws a card for.
	if (flightGeneration !== generation) return false;
	setVideo(blob);
	await runUpload(blob, target);
	return true;
}

installUnloadGuard(useRecorderStore);
